// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Clock, ChevronRight, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'Basi': '#3D7A52',
  'Investimenti': '#5B8DB8',
  'Casa': '#C9A84C',
  'Lavoro': '#E87040',
  'Previdenza': '#8B6FD4',
  'Sicurezza': '#E84040',
  'Finanza Personale': '#C9A84C',
};

const CATEGORIES = ['Tutte', 'Basi', 'Investimenti', 'Casa', 'Lavoro', 'Previdenza', 'Sicurezza'];

const BlogCard = ({ post, featured = false }) => {
  const color = CATEGORY_COLORS[post.category] || '#C9A84C';
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
    >
      <Link to={`/blog/${post.slug}`} className="block h-full" data-testid={`blog-card-${post.post_id}`}>
        <div className={`sapiens-card h-full p-6 transition-all hover:border-[rgba(201,168,76,0.3)] ${featured ? 'border-[rgba(201,168,76,0.2)]' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${color}15`, color }}>
              {post.category}
            </span>
            {post.featured && (
              <span className="text-xs font-medium text-[#C9A84C]">In evidenza</span>
            )}
          </div>
          <h2 className={`font-semibold text-[#E8E8E4] leading-snug mb-3 ${featured ? 'text-xl' : 'text-base'}`}>
            {post.title}
          </h2>
          <p className="text-sm text-[#888780] leading-relaxed mb-4 line-clamp-3">
            {post.excerpt}
          </p>
          <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/[0.06]">
            <div className="flex items-center gap-1.5 text-xs text-[#555553]">
              <Clock className="w-3.5 h-3.5" />
              {post.read_time} min
            </div>
            <span className="text-xs font-medium text-[#C9A84C] flex items-center gap-1">
              Leggi <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('Tutte');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/blog/posts?limit=50`);
        if (res.ok) {
          const data = await res.json();
          setPosts(data.posts || []);
        }
      } catch (e) {
        console.error('Blog fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const filtered = activeCategory === 'Tutte' ? posts : posts.filter(p => p.category === activeCategory);
  const featured = filtered.filter(p => p.featured);
  const regular = filtered.filter(p => !p.featured);

  return (
    <div className="min-h-screen bg-[#1C1C1A]">
      <Helmet>
        <title>Blog — Educazione Finanziaria SAPIENS</title>
        <meta name="description" content="Articoli di educazione finanziaria: ETF, mutuo, pensione, busta paga, FIRE e molto altro. Gratuiti, senza gergo." />
        <link rel="canonical" href="https://finsapiens.it/blog" />
      </Helmet>

      <div className="container-wide py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <BookOpen className="w-6 h-6 text-[#C9A84C]" />
            <span className="text-sm font-medium text-[#C9A84C] uppercase tracking-wider">Blog</span>
          </div>
          <h1 className="text-4xl font-semibold text-[#E8E8E4] mb-2">Educazione finanziaria</h1>
          <p className="text-[#888780] text-lg">Articoli pratici, senza gergo, scritti per chi parte da zero.</p>
        </motion.div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeCategory === cat ? 'text-[#3A2800]' : 'text-[#888780] border border-white/10'}`}
              style={{ background: activeCategory === cat ? '#C9A84C' : '#252523' }}
              data-testid={`blog-filter-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[#888780]">
            <p className="text-lg">Nessun articolo in questa categoria.</p>
          </div>
        ) : (
          <>
            {/* Featured posts */}
            {featured.length > 0 && (
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-[#C9A84C] uppercase tracking-wider mb-4">In evidenza</h2>
                <div className="grid md:grid-cols-3 gap-5">
                  {featured.map(post => (
                    <BlogCard key={post.post_id} post={post} featured />
                  ))}
                </div>
              </div>
            )}

            {/* Regular posts */}
            {regular.length > 0 && (
              <div>
                {featured.length > 0 && <h2 className="text-sm font-semibold text-[#888780] uppercase tracking-wider mb-4">Tutti gli articoli</h2>}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {regular.map(post => (
                    <BlogCard key={post.post_id} post={post} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
